import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../../components/button";
import { Controller } from "react-hook-form";
import TextInput from "../../../components/inputs/TextInput";
import { cn } from "../../../utils/cn";
import { Eye, EyeOff } from "lucide-react";
import LockAccountModal from "../../../components/modal/LockAccountModal";
import {
  useCaptchaLoginUser,
  useGetCaptchaImage,
  useGetUserPrivileges,
} from "../../../hooks/user/mutation";
import useAuthStore from "../../../store/Auth";
import { useGetCaptcha } from "../../../hooks/user/query";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { LoginSchema } from "../../../schemas/loginSchema";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ACCOUNT_LOCKOUT_STATUSES } from "../../../constants/common";
import { AUTH_ROUTES } from "../../../routes";
import { useNavigate } from "react-router-dom";
import { DecodedUser } from "../../../hooks/user/interface";
import { jwtDecode } from "jwt-decode";
import StatusCodes from "../../../constants/StatusCodes";
import {
  blobToObjectURL,
  formattedDateTimeWithoutSec,
} from "../../../lib/functions";
import CheckboxInput from "../../../components/inputs/CheckboxInput";
import Captcha from "../../../components/captcha";

interface FormData {
  username: string;
  password: string;
}

interface LoginFormProps {
  handleNavigateToDefaultPage: (
    privileges: any,
    decodedUser?:
      | {
          RoleId: number;
        }
      | undefined,
  ) => void;
}

const LoginForm = ({ handleNavigateToDefaultPage }: LoginFormProps) => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const [userCaptchaInput, setUserCaptchaInput] = useState<string>("");

  const {
    data: captcha,
    isPending: isCaptchaLoading,
    refetch: refetchCaptcha,
  } = useGetCaptcha();

  const [captchaImage, setCaptchaImage] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useFormWithSchema(LoginSchema);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [accountLockDetails, setAccountLockDetails] = useState<{
    accountLockoutEndTime?: string | null;
    accountLockoutStatus?: number;
    remainingFailedLoginAttempts?: number;
  }>({});
  const [isLockAccountModalOpen, setIsLockAccountModalOpen] = useState(false);

  const {
    mutateAsync: CaptchaLoginUser,
    isPending: isCaptchaLoginUserLoading,
  } = useCaptchaLoginUser();

  const { setIsLoggedIn, setUserRoles } = useAuthStore();

  const { mutateAsync: GetUserPrivileges } = useGetUserPrivileges();

  const { mutateAsync: GetCaptchaImage } = useGetCaptchaImage();

  const handleGetCaptchaImage = useCallback(async () => {
    try {
      const body = {
        url: captcha.dntCaptchaImgUrl,
      };

      const res = await GetCaptchaImage(body);

      const blob = new Blob([res], { type: "image/png" });
      const url = blobToObjectURL(blob);
      setCaptchaImage(url);
    } catch (error) {
      refetchCaptcha();
      console.error("Error fetching captcha image", error);
    }
  }, [GetCaptchaImage, captcha, refetchCaptcha]);

  useEffect(() => {
    if (captcha) {
      handleGetCaptchaImage();
    }
  }, [captcha, handleGetCaptchaImage]);

  useEffect(() => {
    const savedUsername = localStorage.getItem("username");
    const savedEncryptedPassword = localStorage.getItem("password");

    if (savedUsername && savedEncryptedPassword) {
      setValue("username", savedUsername);
      setValue("password", savedEncryptedPassword);
      setRememberMe(true);
    }
  }, [setValue]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!captcha) return;

      if (!userCaptchaInput) {
        toast.error(t("verifyNotARobot"));
        return;
      }

      try {
        const body = new FormData();
        body.append("username", data.username);
        body.append("password", data.password.trim());
        body.append("DNTCaptchaText", captcha?.dntCaptchaTextValue);
        body.append("DNTCaptchaInputText", userCaptchaInput);
        body.append("DNTCaptchaToken", captcha?.dntCaptchaTokenValue);

        const res = await CaptchaLoginUser(body);

        if (
          res?.accountLockoutStatus === ACCOUNT_LOCKOUT_STATUSES.OTP_REQUIRED
        ) {
          navigate(AUTH_ROUTES.OTP, {
            state: {
              username: data.username,
              password: data.password.trim(),
            },
          });
        }

        if (rememberMe) {
          localStorage.setItem("username", data.username);
          localStorage.setItem("password", data.password.trim());
        } else {
          localStorage.removeItem("username");
          localStorage.removeItem("password");
        }

        if (res.token) {
          const decodedUser: DecodedUser = jwtDecode(res.token);

          if (decodedUser?.RoleId) {
            const privileges = await GetUserPrivileges();

            setUserRoles(privileges);

            if (res?.token) {
              setIsLoggedIn(true);

              handleNavigateToDefaultPage(privileges, decodedUser);
            }
          }
        }
      } catch (error: any) {
        refetchCaptcha();
        setUserCaptchaInput("");
        if (error.status === StatusCodes.UNAUTHORIZED) {
          if (!error.result || error.result.remainingFailedLoginAttempts > 0) {
            toast.error(t("incorrectCredentials"));
          }

          setAccountLockDetails(error.result);
          setIsLockAccountModalOpen(
            error.result?.remainingFailedLoginAttempts === 0,
          );
        } else if (error.result && !error.result.IsCaptchaValid) {
          toast.error(t("incorrectCaptcha"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
        console.error("Error logging in user", error);
      }
    },
    [
      refetchCaptcha,
      captcha,
      userCaptchaInput,
      t,
      CaptchaLoginUser,
      rememberMe,
      navigate,
      GetUserPrivileges,
      setUserRoles,
      setIsLoggedIn,
      handleNavigateToDefaultPage,
    ],
  );

  const handleTogglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((prev) => !prev);
  }, []);

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-1 -mt-3"
      >
        <Controller
          name="username"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextInput
              label={t("username")}
              onChange={(value) => {
                onChange(value);
                setAccountLockDetails({});
              }}
              className="input-fields text-black-tertiary"
              error={errors?.[name]?.message}
              labelStyle="text-sm text-black-tertiary"
              errorStyle="-bottom-[15px]"
              value={value}
            />
          )}
        />
        <div className="relative flex flex-col gap-1">
          <div
            className={cn(
              "absolute top-0  z-10 flex items-center gap-[10.9px] cursor-pointer",
              i18n.language === "en" ? "right-0" : "left-0",
            )}
            onClick={handleTogglePasswordVisibility}
          >
            {isPasswordVisible ? (
              <EyeOff className="w-5 h-5 text-black-tertiary/80" />
            ) : (
              <Eye className="w-5 h-5 text-black-tertiary/80" />
            )}
            <p className="text-black-tertiary/80 text-base">
              {isPasswordVisible ? t("hide") : t("show")}
            </p>
          </div>
          <Controller
            name="password"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                type={isPasswordVisible ? "text" : "password"}
                label={t("password")}
                onChange={onChange}
                className="input-fields text-black-tertiary"
                error={errors?.[name]?.message}
                labelStyle="text-sm text-black-tertiary"
                errorStyle="-bottom-[15px]"
                value={value}
              />
            )}
          />

          {accountLockDetails?.accountLockoutEndTime ? (
            <p className="text-sm text-red-500">
              {t("yourAccountWillUnlockAt")}{" "}
              {formattedDateTimeWithoutSec(
                accountLockDetails?.accountLockoutEndTime,
              )}
              .
            </p>
          ) : (
            <></>
          )}

          {accountLockDetails?.remainingFailedLoginAttempts ? (
            <p className="text-sm text-red-500">
              {t(
                `${t("youHave")} ${
                  accountLockDetails?.remainingFailedLoginAttempts
                } ${
                  accountLockDetails?.remainingFailedLoginAttempts > 1
                    ? t("attempts")
                    : t("attempt")
                } ${t("remaining")}`,
              )}
            </p>
          ) : (
            <></>
          )}
        </div>

        <Captcha
          captchaImage={captchaImage}
          isLoading={isCaptchaLoading}
          userCaptchaInput={userCaptchaInput}
          setUserCaptchaInput={setUserCaptchaInput}
          refetchCaptcha={refetchCaptcha}
        />
        <div className="flex items-center gap-2  py-1">
          <CheckboxInput
            key="remember-me"
            id="remember-me"
            label={t("rememberMe")}
            checked={rememberMe}
            setChecked={() => setRememberMe((prev) => !prev)}
          />
        </div>

        <div className="flex flex-col items-center md:gap-2">
          <Button
            className=" text-slate-50 p-4 rounded-xl font-bold w-full"
            isLoading={isCaptchaLoginUserLoading}
          >
            {t("login")}
          </Button>

           <p>
              {t("ifYouAreAVisitor")},{" "}
              <a
                href={process.env.REACT_APP_EXTERNAL_APP_URL}
                className="underline text-sm sm:text-base"
              >
                {t("clickHere")}
              </a>
              !
            </p>
        </div>
      </form>

      <LockAccountModal
        isOpen={isLockAccountModalOpen}
        onOpenChange={() => setIsLockAccountModalOpen(false)}
      />
    </>
  );
};

export default LoginForm;
